<div class="org">
  <header class="org-header">
    <p class="org-eyebrow">Organization</p>
    <h1 class="org-title">Teams</h1>
    <p class="org-subtitle">
      Members only see the projects of their teams. The owner and admins see every project.
      <a routerLink="/knowledge-base/getting-started/invite-your-team" fragment="teams" class="org-link">How teams work</a>
    </p>
    <nav class="org-tabs" aria-label="Organization">
      <a routerLink="/main/organization" routerLinkActive="is-active" [routerLinkActiveOptions]="{ exact: true }">Members</a>
      <a routerLink="/main/organization/teams" routerLinkActive="is-active" data-testid="org-tab-teams">Teams</a>
    </nav>
  </header>

  @if (loading()) {
    <div class="o-card o-card--skeleton">
      <div class="sk sk--title"></div>
      <div class="sk"></div>
      <div class="sk sk--short"></div>
    </div>
  } @else if (loadError()) {
    <div class="o-card o-empty">
      <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
      <p>Couldn’t load the teams.</p>
    </div>
  } @else {

    @if (isOwner()) {
      <section class="o-card">
        <h2 class="o-card__title">Create a team</h2>
        <form class="o-invite" (submit)="$event.preventDefault(); create()">
          <input class="o-input" type="text" maxlength="100" placeholder="e.g. Acme client, Mobile, Backend"
            aria-label="Team name" data-testid="team-new-name"
            [value]="newTeamName()" (input)="newTeamName.set($any($event.target).value); createError.set(null)">
          <button class="pw-btn pw-btn--primary" type="submit" [disabled]="!newTeamName().trim() || creating()"
            data-testid="team-create">
            <i class="fa fa-plus" aria-hidden="true"></i> Create team
          </button>
        </form>
        @if (createError()) {
          <p class="o-error" role="alert">{{ createError() }}</p>
        }
      </section>
    } @else {
      <section class="o-card o-note" data-testid="teams-note">
        <i class="fa fa-info-circle" aria-hidden="true"></i>
        <p>
          @if (seesAll()) {
            As an admin you see every project. The owner manages teams.
          } @else {
            You see the projects of the teams below. Ask the owner to add you to another team.
          }
        </p>
      </section>
    }

    @for (team of teams(); track team.uuid) {
      <section class="o-card t-team" data-testid="team-card">
        @if (editing() === team.uuid && draft(); as d) {
          <div class="t-edit">
            <label class="t-label" for="team-name-{{ team.uuid }}">Name</label>
            <input class="o-input" id="team-name-{{ team.uuid }}" type="text" maxlength="100" data-testid="team-edit-name"
              [value]="d.name" (input)="setDraftName($any($event.target).value)">

            <div class="t-columns">
              <fieldset class="t-pick">
                <legend>Members <span class="o-count">{{ d.members.size }}</span></legend>
                @for (member of members(); track member.uuid) {
                  <label class="t-check">
                    <input type="checkbox" [checked]="d.members.has(member.uuid)" (change)="toggle('members', member.uuid)"
                      data-testid="team-member-option">
                    <span>{{ fullName(member) }}</span>
                    @if (member.role !== 'member') {
                      <span class="o-role o-role--{{ member.role }}">{{ member.role === 'owner' ? 'Owner' : 'Admin' }}</span>
                    }
                  </label>
                }
              </fieldset>
              <fieldset class="t-pick">
                <legend>Projects <span class="o-count">{{ d.projects.size }}</span></legend>
                @for (project of projects(); track project.uuid) {
                  <label class="t-check">
                    <input type="checkbox" [checked]="d.projects.has(project.uuid!)" (change)="toggle('projects', project.uuid!)"
                      data-testid="team-project-option">
                    <span>{{ project.name }}</span>
                  </label>
                } @empty {
                  <p class="o-hint">No projects yet.</p>
                }
              </fieldset>
            </div>

            <div class="t-actions">
              <button class="pw-btn pw-btn--primary" type="button" [disabled]="saving() || !d.name.trim()" (click)="save(team)"
                data-testid="team-save">Save team</button>
              <button class="pw-btn pw-btn--ghost" type="button" (click)="editing.set(null)">Cancel</button>
              <span class="t-spacer"></span>
              @if (confirmingDelete() === team.uuid) {
                <span class="o-row__confirm">Delete {{ team.name }}? Its members lose access to its projects.</span>
                <button class="pw-btn pw-btn--danger" type="button" [disabled]="saving()" (click)="remove(team)"
                  data-testid="team-delete-confirm">Delete</button>
              } @else {
                <button class="pw-btn pw-btn--danger-outline" type="button" (click)="confirmingDelete.set(team.uuid)"
                  data-testid="team-delete">Delete team</button>
              }
            </div>
          </div>
        } @else {
          <div class="t-head">
            <h2 class="o-card__title">{{ team.name }}</h2>
            @if (isOwner()) {
              <button class="pw-btn pw-btn--ghost pw-btn--sm" type="button" (click)="startEditing(team)" data-testid="team-edit">
                <i class="fa fa-pencil" aria-hidden="true"></i> Edit
              </button>
            }
          </div>
          <div class="t-columns">
            <div>
              <p class="t-label">Members <span class="o-count">{{ team.members.length }}</span></p>
              <div class="t-chips" data-testid="team-members">
                @for (member of team.members; track member.uuid) {
                  <span class="t-chip"><span class="t-chip__avatar">{{ (member.first_name || member.email)[0] }}</span>{{ fullName(member) }}</span>
                } @empty {
                  <span class="o-hint">No members yet.</span>
                }
              </div>
            </div>
            <div>
              <p class="t-label">Projects <span class="o-count">{{ team.projects.length }}</span></p>
              <div class="t-chips" data-testid="team-projects">
                @for (project of team.projects; track project.uuid) {
                  <a class="t-chip t-chip--project" routerLink="/main/project/{{ project.uuid }}/errors">{{ project.name }}</a>
                } @empty {
                  <span class="o-hint">No projects yet.</span>
                }
              </div>
            </div>
          </div>
        }
      </section>
    } @empty {
      <section class="o-card o-empty" data-testid="teams-empty">
        <i class="fa fa-users" aria-hidden="true"></i>
        <p>
          @if (isOwner()) {
            No teams yet. Create one per client or product, then add people and projects to it.
          } @else {
            You’re not in a team yet, so you don’t see any projects. Ask the owner to add you to one.
          }
        </p>
      </section>
    }

    @if (seesAll() && unassignedProjects().length) {
      <section class="o-card" data-testid="teams-unassigned">
        <h2 class="o-card__title">Not in any team <span class="o-count">{{ unassignedProjects().length }}</span></h2>
        <p class="o-hint">Only the owner and admins see these projects. Add them to a team to share them with its members.</p>
        <div class="t-chips">
          @for (project of unassignedProjects(); track project.uuid) {
            <span class="t-chip t-chip--project">{{ project.name }}</span>
          }
        </div>
      </section>
    }
  }
</div>
